<script>
  import { onMount, onDestroy } from "svelte";
  import { tourFunctions } from "../tour-functions";
  import { fly } from "svelte/transition";
  import i18n from "./i18n/i18n";
  import Audio from "./Audio.svelte";
  import InfoIcon from "./icons/InfoIcon.svelte";
  import NoDescriptionIcon from "./icons/NoDescriptionIcon.svelte";
  import Tooltip from "./Tooltip.svelte";
  import PersonalizationButton from "./PersonalizationButton.svelte";

  // Props
  export let tour;
  export let playing = true;
  export let loop = false;
  export let resumable = true;
  export let debug = false;
  export let enableSceneAudio = true;
  export let enableDescriptionAudio = true;
  export let onPlayingChange = null;
  export let showDescriptionOnMobile = false;
  export let hasPersonalizationButton = false;
  export let onPersonalize = null;

    // Mobile detection
  import UA from "ua-parser-js";
  const ua = new UA();
  let isMobile = ua.getResult().device["type"] == "mobile";

  // State
  let title = null;
  let description = null;
  let isVisible = !isMobile;
  let showTooltip = false;
  let currentScene = null;
  let hasDescriptionAudio = false;
  let isAudioInitialized = false;
  let windowSize = { width: 0, height: 0 };
  let audioComponent;



  // Initialize window size
  onMount(() => {
    if (typeof window !== 'undefined') {
      windowSize = {
        width: window.innerWidth,
        height: window.innerHeight
      };
    }
  });

  // Window resize listener
  const handleResize = () => {
    windowSize = {
      width: window.innerWidth,
      height: window.innerHeight
    };
  };

  onMount(() => {
    window.addEventListener('resize', handleResize);
  });

  onDestroy(() => {
    window.removeEventListener('resize', handleResize);
  });

  // Initialize audio on first user interaction
  const handleUserInteraction = async () => {
    if (!isAudioInitialized && audioComponent && audioComponent.init) {
      await audioComponent.init();
      isAudioInitialized = true;
    }
  };

  onMount(() => {
    const events = ['click', 'touchstart', 'keydown'];
    events.forEach(event => {
      document.addEventListener(event, handleUserInteraction, { once: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserInteraction);
      });
    };
  });

  // Update description when scene changes
  const updateSceneDescription = () => {
    const sceneId = tourFunctions.currentScene.getId();
    const scene = tour?.scenes.find(scene => scene.id === sceneId);
    if (!scene) {
      title = null;
      description = null;
      hasDescriptionAudio = false;
      return;
    }

    currentScene = scene;
    hasDescriptionAudio = !!scene?.descriptionAudio?.assetId;

    if (!scene || !scene.description || scene.description.trim() === '') {
      title = null;
      description = null;
      return;
    }

    // Parse the content with basic HTML replacement
    let content = null;
    try {
      // Try to parse as JSON (for tiptap content)
      const contentJson = JSON.parse(scene.description);
      // For now, just use the raw text from tiptap content
      // In a full implementation, you'd want to use a tiptap HTML generator
      content = contentJson.content?.[0]?.content?.[0]?.text || scene.description;
    } catch (e) {
      // If not valid JSON, use as plain text with basic HTML replacement
      content = tourFunctions.parseContent(scene.description);
    }
    
    title = tourFunctions.parseContent(scene.title);
    description = content;
  };

  // Handle scene changes
  const handleNewPano = (e) => {
    if (isMobile) isVisible = false;
    updateSceneDescription();
  };

  onMount(() => {
    updateSceneDescription();
    window.addEventListener('krpano:newpano', handleNewPano);
  });

  onDestroy(() => {
    window.removeEventListener('krpano:newpano', handleNewPano);
  });

  // Check if tour has any audio
  $: hasTourAudio = tour?.scenes?.some(scene => scene.audioId || scene.descriptionAudio?.assetId) || false;
  
  // Determine if we should show just audio or full description
  $: showFullDescription = tour?.features?.includes('sceneDescriptions') && (!isMobile || showDescriptionOnMobile);
  $: showAudioOnly = hasTourAudio && !showFullDescription;
</script>

{#if showFullDescription}
  <!-- Full Scene Description with Audio -->
  <div class="scene-description-container">
    {#if isVisible && description}
      <div 
        class="description-box"
        transition:fly={{ y: 20, duration: 500 }}
      >
        <div class="description-header">
          <h3 class="description-title">
            {@html title}
          </h3>
        </div>
        <div class="description-content">{@html description}</div>
      </div>
    {/if}

    <div class="controls-container">
      <!-- Personalization Button (First) -->
      {#if hasPersonalizationButton}
        <div class="personalization-container">
          <PersonalizationButton on:personalize={onPersonalize} />
        </div>
      {/if}

      <!-- Description Toggle with Tooltip -->
      {#if description}
        <div class="tooltip-container">
          <button
            class="description-toggle"
            on:click={() => isVisible = !isVisible}
            on:mouseenter={() => showTooltip = true}
            on:mouseleave={() => showTooltip = false}
            on:focus={() => showTooltip = true}
            on:blur={() => showTooltip = false}
            aria-label={description ? (isVisible ? 'Hide scene description' : 'Show scene description') : 'No description available'}
          >
            <InfoIcon />
          </button>

          <Tooltip 
            showingTooltip={showTooltip}
            text={description 
              ? (isVisible ? 'Hide Description' : 'Show Description')
              : 'No Description Available'}
            position="right"
          />
        </div>
      {/if}

      <!-- Audio Player (Last) -->
      {#if hasTourAudio}
        <Audio
          bind:this={audioComponent}
          {tour}
          {playing}
          {loop}
          {resumable}
          {debug}
          {enableSceneAudio}
          {enableDescriptionAudio}
          {onPlayingChange}
        />
      {/if}
    </div>
  </div>
{:else if showAudioOnly}
  <!-- Audio Only - positioned like in Next.js -->
  <div class="audio-only-container">
    <div class="audio-only-controls">
      <!-- Personalization Button (First) -->
      {#if hasPersonalizationButton}
        <div class="personalization-container">
          <PersonalizationButton on:personalize={onPersonalize} />
        </div>
      {/if}
      
      <Audio
        bind:this={audioComponent}
        {tour}
        {playing}
        {loop}
        {resumable}
        {debug}
        {enableSceneAudio}
        {enableDescriptionAudio}
        {onPlayingChange}
      />
    </div>
  </div>
{/if}

<style>
  .scene-description-container {
    position: fixed;
    bottom: 0;
    left: 10px;
    z-index: 99;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .audio-only-container {
    position: fixed;
    bottom: 10px;
    left: 10px;
    z-index: 99;
  }

  .description-box {
    background-color: rgba(0, 0, 0, 0.8);
    color: #fff;
    padding: 1rem;
    border-radius: 8px;
    font-family: var(--font-body), sans-serif;
    font-size: 14px;
    line-height: 1.5;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
    min-width: 150px;
    min-height: 150px;
    width: 30vw;
    height: 30vh;
    max-width: 350px;
    max-height: 300px;
    overflow-y: auto;
    pointer-events: auto;
    box-sizing: border-box;
  }

  

  .description-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    padding-bottom: 0.5rem;
  }

  .description-title {
    font-family: var(--font-heading), sans-serif;
    font-size: 18px;
    font-weight: bold;
    margin: 0;
  }

  .controls-container {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    border-radius: 8px;
    margin-bottom: 0;
  }

  .audio-only-controls {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .personalization-container {
    display: flex;
    align-items: center;
  }

  /* Override PersonalizationButton positioning when inside SceneDescription */
  .personalization-container :global(.personalization-button) {
    position: static !important;
    bottom: auto !important;
    left: auto !important;
    margin: 0 !important;
  }

  /* Override Audio component positioning when in full description mode */
  .controls-container :global(.container) {
    position: static !important;
    margin: 0 !important;
  }

  /* Override Audio component positioning when in audio-only mode */
  .audio-only-controls :global(.container) {
    position: static !important;
    margin: 0 !important;
  }

  .tooltip-container {
    position: relative;
    display: flex;
    align-items: center;
  }

  /* Ensure tooltip positions correctly relative to the button */
  .tooltip-container :global(.tooltip) {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1000;
  }

  .description-toggle {
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .description-toggle:hover {
    opacity: 0.8;
  }


  /* Scrollbar styles */
  :global(.description-box::-webkit-scrollbar) {
    width: 5px;
  }
  
  :global(.description-box::-webkit-scrollbar-thumb) {
    background: #ffffff;
    border-radius: 10px;
  }

  :global(.description-box::-webkit-scrollbar-thumb:hover) {
    background: #AAAAAA;
  }


  /* Mobile responsive styles */
  @media (max-width: 768px) {
    .scene-description-container {
      left: 0;
      right: 0;
      bottom: 0;
      padding-inline: 20px;
    }

    .description-box {
      width: 100%;
      height: 25vh;
      min-width: auto;
      min-height: 120px;
      max-width: none;
      max-height: 200px;
      font-size: 13px;
      padding: 0.75rem;
    }

    .controls-container {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
        padding: 0;
        padding-block: 8px;
        margin-bottom: 0;
        width: 100%;
        box-sizing: border-box;
    }

    .audio-only-controls {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
        width: 100%;
    }

  }

  /* Mobile responsive adjustments */
  @media (max-width: 768px) {
    .description-box {
      width: 80vw;
      max-width: none;
      height: 25vh;
      max-height: 200px;
    }

    .description-box {
      width: 100%;
      height: fit-content;
      max-height: 21vh;
      margin-bottom: 50px;
      /* margin-bottom: 1rem; */
    }
  }
</style> 